// This part of the form will collect the Available data and Due Date of the job
import React, { Component } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import 'moment-timezone';
import { HTTPService } from "../services/httpService";
import {
  todayInLocationTz,
  isDateAvailable,
  findNextAvailableDate,
} from '../util/dateAvailability';

const API = new HTTPService();

export default class DatesForm extends Component {

  state = {
    time: new Date(),
    isOpen: false,
    errors: {},
    vehicle: this.props.vehicle,
    availability: null,        // Store availability data
    isLoadingAvailability: false,  // Loading state
    closedDays: [],            // Day-of-week numbers where location is closed (0=Sunday)
    blockedDates: []           // Specific dates that are blocked (holidays, vacations)
  }

  addDays(date, days) {
    let result = moment(date).format("YYYY-MM-DD");
    let results = moment(result).add(days, "days");
    return results;
  }

  // Method to fetch availability (existing)
  fetchAvailability = async () => {
    if (!this.props.typeId) return;
    
    this.setState({ isLoadingAvailability: true });
    
    try {
      //console.log("Services for duration calculation:", this.props.services);
      
      const totalDuration = this.props.services.reduce((total, service) => {
        //console.log(`Service: ${service.description}, duration_days: ${service.duration_days}`);
        return total + (service.duration_days || 1);
      }, 0);
      
      //console.log("Calculated total duration:", totalDuration);
      
      // HTTPService automatically handles JSON parsing
      const locationParam = this.props.selectedLocationId ? `&locationId=${this.props.selectedLocationId}` : '';
      const { data: availability } = await API.get(`/availability/${this.props.typeId}?duration=${totalDuration}${locationParam}`);

      //console.log("Availability response:", availability);
      // Convert blockedDates strings to Date objects for DatePicker excludeDates prop
      const blockedDateObjects = (availability.blockedDates || []).map(dateStr =>
        moment(dateStr).toDate()
      );

      this.setState({
        availability,
        closedDays: availability.closedDays || [],
        blockedDates: blockedDateObjects,
        isLoadingAvailability: false
      });
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      this.setState({ availability: null, isLoadingAvailability: false });
    }
  }

  // NEW: Method to fetch availability for specific date
  fetchAvailabilityForDate = async (selectedDate) => {
    if (!this.props.typeId) return;
    
    this.setState({ isLoadingAvailability: true });
    
    try {
      const totalDuration = this.props.services.reduce((total, service) => {
        return total + (service.duration_days || 1);
      }, 0);
      
      // Include the selected date in the request
      const dateParam = moment(selectedDate).format('YYYY-MM-DD');
      //console.log("Checking availability for specific date:", dateParam);
      
      const locationParam = this.props.selectedLocationId ? `&locationId=${this.props.selectedLocationId}` : '';
      const { data: availability } = await API.get(
        `/availability/${this.props.typeId}?duration=${totalDuration}&checkDate=${dateParam}${locationParam}`
      );

      //console.log("Availability response for date:", availability);
      // Convert blockedDates strings to Date objects for DatePicker excludeDates prop
      const blockedDateObjects = (availability.blockedDates || []).map(dateStr =>
        moment(dateStr).toDate()
      );

      this.setState({
        availability,
        closedDays: availability.closedDays || [],
        blockedDates: blockedDateObjects,
        isLoadingAvailability: false
      });
    } catch (error) {
      console.error('Failed to fetch availability for date:', error);
      // Fallback to general availability if specific date check fails
      await this.fetchAvailability();
    }
  }

  // NEW: Enhanced date change handler
  handleDateChange = async (date) => {
    //console.log("Date changed to:", date);
    
    // Update the parent's date immediately
    this.props.handleAvailableDate(date);
    
    // Re-fetch availability for the new date
    await this.fetchAvailabilityForDate(date);
  }

  // Call availability on component mount
  componentDidMount() {
    this.fetchAvailability();
  }

  // Refetch if services change
  componentDidUpdate(prevProps, prevState) {
    // Refetch availability if services or installer changes
    if (prevProps.services !== this.props.services || prevProps.typeId !== this.props.typeId) {
      this.fetchAvailability();
    }
    
    // Update parent's date when availability arrives (only once).
    // Two conditions trigger an override:
    //   (a) The current date is in the past — bump forward to suggestion
    //   (b) The current date falls on a closed day or blocked date —
    //       bump to next available (in the location's timezone)
    if (!prevState.availability && this.state.availability) {
      const tz = this.props.currentLocation?.timezone;
      const currentDate = moment(this.props.availableDate);
      const blockedStrings = (this.state.availability.blockedDates || []);
      const closedDays = this.state.availability.closedDays || [];

      const isPast = currentDate.isBefore(todayInLocationTz(tz));
      const onClosedDay = !isDateAvailable(currentDate.toDate(), closedDays, blockedStrings);

      if (isPast || onClosedDay) {
        // Prefer server's nextStart suggestion if it's also available.
        let nextDate = this.state.availability.nextStart
          ? moment(this.state.availability.nextStart)
          : findNextAvailableDate(todayInLocationTz(tz), closedDays, blockedStrings);
        if (!isDateAvailable(nextDate.toDate(), closedDays, blockedStrings)) {
          nextDate = findNextAvailableDate(todayInLocationTz(tz), closedDays, blockedStrings);
        }
        this.props.handleAvailableDate(nextDate.toDate());
      }
    }
  }

  render() {

    if (this.props.currentStep !== 5) { // Prop: The current step
      return null;
    }

    // Walk-ins are by definition same-day — allow today as a valid selection.
    // Anchor to the LOCATION's timezone so EDT viewer of an HST shop uses
    // HST's "today" (a late-night EDT user might still see HST as same-day).
    let minDate = todayInLocationTz(this.props.currentLocation?.timezone).toDate();

    let availableDate = new moment(this.props.availableDate).toDate();
    
    // Use availability suggestion only if the current date is in the past
    if (this.state.availability && this.state.availability.nextStart) {
      const suggestedDate = new moment(this.state.availability.nextStart).toDate();
      if (moment(availableDate).isBefore(moment().startOf('day'))) {
        availableDate = suggestedDate;
      }
    }

    let availDateDisplay = moment(availableDate).format("MMMM DD, YYYY");
    
    // Calculate total job duration for display
    const totalJobDays = this.props.services.reduce((total, service) => {
      return total + (service.duration_days || 1);
    }, 0);

    //console.log("Vehicle :", this.props.vehicle);
    let displayVehicle = this.props.vehicle.year + " " + this.props.vehicle.make +" "+ this.props.vehicle.model+" "+ this.props.vehicle.trim;
    
    // build the display services string for the selected services
    let displayServices = this.props.services[0].service_category + " " + this.props.services[0].description;
    for (var x=1; x < this.props.services.length; x++){
        if (x === this.props.services.length - 1)
            displayServices = displayServices + " & ";
         else
            displayServices = displayServices + ", ";
        displayServices = displayServices + this.props.services[x].service_category + " " + this.props.services[x].description
    }

    return (
      <React.Fragment>
        <form className="form-container p-5 justify-content-md-center">

          {/* UPDATED: Enhanced Availability Information */}
          {this.state.availability && !this.state.isLoadingAvailability && (
            <div className="form-row justify-content-md-center mb-3">
              <div className="col-md-8">
                <div className={`alert ${this.state.availability.meetsDeadline && !this.state.availability.warning ? 'alert-success' : 'alert-warning'}`}>
                  <strong>Your Availability:</strong><br/>
                  Currently available to start: {moment(this.state.availability.nextStart || this.state.availability.requestedDate).format("MMM DD, YYYY")}
                  {totalJobDays > 1 && (
                    <span> ({totalJobDays}-day job)</span>
                  )}
                  
                  {this.state.availability.meetsDeadline && !this.state.availability.warning && (
                    <div><small className="text-muted">
                      💡 {this.state.availability.draftConflicts > 0 
                           ? `${this.state.availability.draftConflicts} estimate(s) may be rescheduled to accommodate` 
                           : 'No conflicts with confirmed bookings'}
                    </small></div>
                  )}
                  
                  {this.state.availability.warning && (
                    <div><small className="text-muted">
                      ⚠️ May need to work over normal capacity
                    </small></div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {this.state.isLoadingAvailability && (
            <div className="form-row justify-content-md-center mb-3">
              <div className="col-md-8 text-center">
                <small className="text-muted">
                  <i className="fa-sharp fa-regular fa-spinner fa-spin"></i> Checking availability for selected date...
                </small>
              </div>
            </div>
          )}

          <div className="form-row justify-content-md-center">

            <div className="form-group col-md-2 text-center">
              <label htmlFor="date" className="bold">Select Drop Off Date</label>
            </div>

            <div className="form-group col-md-4 text-center">
              <DatePicker
                name="availableDate"
                selected={availableDate}
                value={availableDate}
                minDate={minDate}
                onChange={this.handleDateChange}  // CHANGED: Use new handler instead of this.props.handleAvailableDate
                excludeDates={[...(this.props.excludeDays || []), ...this.state.blockedDates]}
                filterDate={(date) => {
                  // Filter out days the location is closed (recurring weekly closures)
                  const dayOfWeek = moment(date).day(); // 0=Sunday, 1=Monday, etc.
                  return !this.state.closedDays.includes(dayOfWeek);
                }}
                dateFormat="MMM do yyyy"
                className="form-control"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
        </div>
        </form>
        
        <p className="text-center bold">{displayVehicle}</p>
        <p className="text-center bold">{displayServices}</p>
        <p className="text-center bold">for {this.props.customer}</p>
        
        {this.props.notes && <p className="text-center bold">{this.props.notes}</p>}

        <p className="text-center bold">
          Drop off by end of day on {availDateDisplay}
        </p>
      </React.Fragment>
    );

  }

}